import React, { useState } from 'react';
import { Meeting } from '../../../types/meeting';
import AddIcon from '../../icons/AddIcon';
import SearchIcon from '../../icons/SearchIcon';
import TrashIcon from '../../icons/TrashIcon';
import MoreIcon from '../../icons/MoreIcon';
import Dropdown from '../../ui/Dropdown';
import MeetingStatusBadge from './MeetingStatusBadge';
import { DropdownItem } from '../../../types/dropdown';
import '../../../styles/meetings.css';

interface MeetingsListViewProps {
  meetings: Meeting[];
  /** Live view: show only not-yet-completed meetings (draft/prepared). */
  liveOnly?: boolean;
  /** Secretary/Admin: may create and delete meetings. Others get a read view. */
  canManage?: boolean;
  onCreateMeeting: () => void;
  onViewMeeting: (meeting: Meeting) => void;
  onDeleteMeeting: (meeting: Meeting) => void;
}

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const MeetingCard: React.FC<{
  meeting: Meeting;
  canManage: boolean;
  onClick: () => void;
  onDelete: () => void;
}> = ({ meeting, canManage, onClick, onDelete }) => {
  const items: DropdownItem[] = [
    {
      id: 'delete',
      label: 'Delete',
      icon: <TrashIcon size={18} />,
      onClick: onDelete,
      variant: 'danger'
    }
  ];

  return (
    <div className="meeting-card" onClick={onClick}>
      <div className="meeting-card-header">
        <h3 className="meeting-card-title">{meeting.title}</h3>
        {canManage && (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={<button className="more-button" aria-label="More options"><MoreIcon size={16} /></button>}
              items={items}
            />
          </div>
        )}
      </div>

      <MeetingStatusBadge meeting={meeting} />

      {meeting.description && <p className="meeting-card-desc">{meeting.description}</p>}

      <div className="meeting-card-meta">
        <span className="meeting-card-meta-item">📅 {formatDateTime(meeting.scheduled_at)}</span>
        <span className="meeting-card-meta-item">⏱ {meeting.duration_minutes} min</span>
        {meeting.committee && <span className="meeting-card-meta-item">👥 {meeting.committee}</span>}
        {meeting.location && <span className="meeting-card-meta-item">📍 {meeting.location}</span>}
      </div>

      <div className="meeting-card-meta">
        <span className="meeting-card-meta-item">{meeting.attendees.length} attendees</span>
        <span className="meeting-card-meta-item">
          {meeting.agenda?.items?.length ? `${meeting.agenda.items.length} agenda items` : 'No agenda yet'}
        </span>
        {meeting.live_state?.is_live && (meeting.live_state.participants?.length ?? 0) > 0 && (
          <span className="meeting-card-meta-item">
            👥 {meeting.live_state.participants!.length} joined
          </span>
        )}
      </div>
    </div>
  );
};

const MeetingsListView: React.FC<MeetingsListViewProps> = ({
  meetings,
  liveOnly = false,
  canManage = false,
  onCreateMeeting,
  onViewMeeting,
  onDeleteMeeting
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Live view lists only prepared meetings (ready to run, not yet completed).
  const scoped = liveOnly
    ? meetings.filter((m) => m.status === 'prepared')
    : meetings;

  const filtered = scoped.filter((m) =>
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.committee || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Live view splits prepared meetings into those already running and those
  // still waiting to go live.
  const liveNow = filtered.filter((m) => m.live_state?.is_live === true);
  const notYetLive = filtered.filter((m) => m.live_state?.is_live !== true);

  const renderCard = (meeting: Meeting) => (
    <MeetingCard
      key={meeting.meeting_id}
      meeting={meeting}
      canManage={canManage}
      onClick={() => onViewMeeting(meeting)}
      onDelete={() => onDeleteMeeting(meeting)}
    />
  );

  return (
    <main className="dashboard-content">
      <header className="dashboard-header">
        <div className="header-top">
          <h1 className="main-title">{liveOnly ? 'Live meetings' : 'Meetings'}</h1>
          {canManage && (
            <button className="create-btn" onClick={onCreateMeeting}>
              <AddIcon size={20} /> New Meeting
            </button>
          )}
        </div>
        <p className="subheading">
          {liveOnly
            ? 'Prepared meetings — live now and waiting to start'
            : 'Prepare, run, and review meetings grounded in your documents'}
        </p>
      </header>

      <section className="projects-section">
        {scoped.length > 0 && (
          <div className="search-container">
            <SearchIcon className="search-icon" size={16} />
            <input
              className="search-input"
              type="text"
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}

        {scoped.length === 0 ? (
          liveOnly ? (
            <div className="meeting-empty-hint" style={{ marginTop: 24 }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: 600 }}>No live meetings</p>
              <p style={{ margin: 0 }}>
                Prepared meetings appear here, ready to go live.
              </p>
            </div>
          ) : (
            <div className="meeting-empty-hint" style={{ marginTop: 24 }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: 600 }}>No meetings yet</p>
              <p style={{ margin: '0 0 16px 0' }}>
                {canManage
                  ? 'Create your first meeting to upload materials, generate an agenda, and capture the discussion.'
                  : 'Meetings you are invited to will appear here once a secretary prepares them.'}
              </p>
              {canManage && (
                <button className="meeting-btn meeting-btn-primary" onClick={onCreateMeeting}>
                  <AddIcon size={16} /> Create Meeting
                </button>
              )}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="no-results">No meetings found matching your search.</div>
        ) : liveOnly ? (
          <div className="live-meetings-split">
            <section className="live-meetings-section">
              <div className="live-meetings-section-header">
                <span className="live-meetings-dot live" aria-hidden="true" />
                <h2 className="live-meetings-section-title">Live now</h2>
                <span className="live-meetings-section-count">{liveNow.length}</span>
              </div>
              {liveNow.length === 0 ? (
                <p className="live-meetings-column-empty">No meetings are live right now.</p>
              ) : (
                <div className="meetings-grid">{liveNow.map(renderCard)}</div>
              )}
            </section>
            <section className="live-meetings-section">
              <div className="live-meetings-section-header">
                <span className="live-meetings-dot prepared" aria-hidden="true" />
                <h2 className="live-meetings-section-title">Prepared · not yet live</h2>
                <span className="live-meetings-section-count">{notYetLive.length}</span>
              </div>
              {notYetLive.length === 0 ? (
                <p className="live-meetings-column-empty">No prepared meetings waiting to start.</p>
              ) : (
                <div className="meetings-grid">{notYetLive.map(renderCard)}</div>
              )}
            </section>
          </div>
        ) : (
          <div className="meetings-grid">
            {filtered.map(renderCard)}
          </div>
        )}
      </section>
    </main>
  );
};

export default MeetingsListView;
